import React, { useState, useEffect } from "react";
import {
  ExternalLink,
  ArrowRight,
  Check,
  Car,
  Loader,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";
import analyticsService from "../../lib/AnalyticsService";
import ChaizCouponCard from "./ChaizCouponCard";

const ExtendedWarrantyHandoff = ({ vehicleData, onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [rewardCount, setRewardCount] = useState(0);
  const [showLearnMore, setShowLearnMore] = useState(false);

  const { getIdToken } = useAuth();

  useEffect(() => {
    analyticsService.trackPageView("ew_handoff", {
      screen: "handoff",
      flow_type: "extended-warranty",
      has_vehicle_data: !!vehicleData,
    });
  }, [vehicleData]);

  const handleSubmitToServer = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      analyticsService.trackEvent("Extended Warranty Submission Initiated", {
        has_vin: !!vehicleData?.vin,
        has_make: !!vehicleData?.make,
        has_model: !!vehicleData?.model,
        has_mileage: !!vehicleData?.mileage,
        state: vehicleData?.state,
      });

      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const payload = {
        vin: vehicleData.vin || null,
        year: vehicleData.year || null,
        make: vehicleData.make || null,
        model: vehicleData.model || null,
        mileage: vehicleData.mileage || null,
        state: vehicleData.state || null,
        license: vehicleData.license || null,
      };

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/extended-warranty/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Server error: ${response.status}`
        );
      }

      const responseData = await response.json();

      setRedirectUrl(responseData.redirectUrl);
      setRewardCount(responseData.rewardCount || 0);
      setSubmissionComplete(true);

      sessionStorage.setItem(
        "extendedWarrantySubmission",
        JSON.stringify({
          flowId: responseData.flowId,
          vehicleCount: responseData.vehicleCount,
          rewardCount: responseData.rewardCount,
          submittedAt: new Date().toISOString(),
          redirectUrl: responseData.redirectUrl,
        })
      );

      analyticsService.trackEvent("Extended Warranty Submission Success", {
        flow_id: responseData.flowId,
        vehicle_count: responseData.vehicleCount,
        reward_count: responseData.rewardCount,
        has_redirect_url: !!responseData.redirectUrl,
      });

      return responseData;
    } catch (err) {
      console.error("Error submitting extended warranty data:", err);
      analyticsService.trackError("ew_submission_error", err, {
        screen: "handoff",
      });
      setError(
        err.message || "Failed to submit vehicle data. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = async () => {
    if (isRedirecting) return;

    if (!submissionComplete) {
      try {
        await handleSubmitToServer();
      } catch (error) {
        return;
      }
    }

    const finalRedirectUrl =
      redirectUrl ||
      (() => {
        try {
          return JSON.parse(
            sessionStorage.getItem("extendedWarrantySubmission")
          )?.redirectUrl;
        } catch {
          return null;
        }
      })();

    if (!finalRedirectUrl) {
      console.error("No redirect URL available");
      return;
    }

    setIsRedirecting(true);

    try {
      analyticsService.trackEvent("Extended Warranty Chaiz Redirect", {
        redirect_url_domain: new URL(finalRedirectUrl).hostname,
        reward_count: rewardCount,
      });
    } catch (e) {
      // URL parsing might fail, still proceed
    }

    sessionStorage.setItem(
      "chaizHandoff",
      JSON.stringify({
        vehicleData,
        handoffAt: new Date().toISOString(),
        chaizUrl: finalRedirectUrl,
        rewardCount,
      })
    );

    window.location.href = finalRedirectUrl;
  };

  const formatVehicleInfo = () => {
    const parts = [];
    if (vehicleData.year) parts.push(vehicleData.year);
    if (vehicleData.make) parts.push(vehicleData.make);
    if (vehicleData.model) parts.push(vehicleData.model);
    return parts.join(" ") || "Your vehicle";
  };

  // Loading state
  if (isSubmitting) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <Loader className="w-12 h-12 text-[#7B61FF] animate-spin mx-auto" />
          <h1 className="text-2xl font-bold font-bebas uppercase">
            Processing Your Information
          </h1>
          <p className="text-gray-600 text-sm">
            Saving your vehicle information and preparing your quotes...
          </p>
        </div>

        <div className="bg-[#D4F6C9] rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <Car className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-darkPurple text-sm">
                {formatVehicleInfo()}
              </p>
              <p className="text-sm text-gray-600">
                {vehicleData.mileage &&
                  `${Number(vehicleData.mileage).toLocaleString()} miles`}
                {vehicleData.state && ` \u2022 ${vehicleData.state}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold font-bebas uppercase">
            Something Went Wrong
          </h1>
          <p className="text-gray-600 text-sm">
            We encountered an issue processing your information.
          </p>
        </div>

        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>

        <button
          onClick={() => {
            analyticsService.trackEvent("Extended Warranty Button Click", {
              button_name: "handoff-retry",
              screen: "handoff",
            });
            handleSubmitToServer();
          }}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
        >
          Try Again
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Main view — connecting with Chaiz
  return (
    <div className="space-y-6 p-4">
      {/* Header with Chaiz logo */}
      <div className="text-center space-y-3">
        <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto p-3">
          <img
            className="w-full h-full object-contain"
            src="https://ownlicdn.blob.core.windows.net/assets/chaiz.png"
            alt="Chaiz"
          />
        </div>
        <h1 className="text-2xl font-bold font-bebas uppercase">
          We're connecting you with Chaiz
        </h1>
        <p className="text-gray-600 text-sm">
          Get personalized extended warranty quotes for your vehicle.
        </p>
      </div>

      {/* Provider card */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-4 p-4">
          <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center p-2 flex-shrink-0">
            <img
              className="w-full h-full object-contain"
              src="https://ownlicdn.blob.core.windows.net/assets/chaiz.png"
              alt="Chaiz"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-darkPurple">Chaiz</h2>
            <p className="text-sm text-gray-500">
              Extended warranty comparison platform
            </p>
          </div>
          <button
            onClick={() => setShowLearnMore(!showLearnMore)}
            className="text-[#7B61FF] font-medium cursor-pointer text-sm flex-shrink-0"
          >
            Learn more
          </button>
        </div>

        {/* Expandable "Learn more" section */}
        <div
          className={`overflow-hidden transition-all duration-200 ${
            showLearnMore
              ? "max-h-96 opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="border-t border-gray-200 p-4 space-y-3">
            <p className="text-sm text-gray-700">
              Chaiz helps you shield yourself from unexpected vehicle costs.
              Without a factory warranty, repairs can be costly.
            </p>
            <p className="text-sm font-semibold text-darkPurple">
              The nation's only comparison site for extended vehicle warranties.
            </p>
            <p className="text-sm text-gray-700">
              Compare and buy breakdown protection from top providers within 30
              seconds.
            </p>
            <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
              <li>
                Save 60% on coverage with flexibility to cancel anytime and a
                30-day money-back guarantee
              </li>
              <li>
                Avoid costly repairs — like a $5,000 transmission bill — with
                only a $100 deductible
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Vehicle summary — green card */}
      <div className="bg-[#D4F6C9] rounded-2xl p-4 space-y-1">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-600 mb-2">
          Your Vehicle
        </p>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
          <p className="text-sm font-bold text-darkPurple">
            {formatVehicleInfo()}
          </p>
        </div>
        {(vehicleData.mileage || vehicleData.state || vehicleData.vin) && (
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              {[
                vehicleData.mileage &&
                  `${Number(vehicleData.mileage).toLocaleString()} miles`,
                vehicleData.state,
                vehicleData.vin && `VIN: ${vehicleData.vin}`,
              ]
                .filter(Boolean)
                .join(" \u2022 ")}
            </p>
          </div>
        )}
      </div>

      {/* Reward earned */}
      {submissionComplete && rewardCount > 0 && (
        <div className="bg-[#D4F6C9] rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wide font-bold mb-3 text-gray-600">
            Reward Earned
          </p>
          <div className="flex items-stretch">
            <div className="flex-1 pr-4">
              <p className="text-3xl font-bold font-bebas text-darkPurple">
                ${rewardCount}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Cash earned — your data, your money
              </p>
            </div>
            <div className="border-l border-gray-400 pl-4 flex-1">
              <p className="text-3xl font-bold font-bebas text-darkPurple">
                $1,000+
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Estimated potential annual savings
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Coupon code */}
      <ChaizCouponCard />

      {/* CTA — Get quotes */}
      <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
        <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
          Get Your Quotes
        </p>
        <p className="text-gray-500 text-sm">
          You'll be redirected to Chaiz to see personalized extended warranty
          quotes for your vehicle.
        </p>
        <button
          onClick={handleRedirect}
          disabled={isRedirecting}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
        >
          {isRedirecting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Redirecting...</span>
            </>
          ) : (
            <>
              <span>Get My Warranty Quotes</span>
              <ExternalLink className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* What Happens Next */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
          What Happens Next
        </p>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">1</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Review your pre-filled info</span> —
              your vehicle details are already loaded.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">2</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Compare warranty plans</span> — see
              quotes from multiple providers side by side.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">3</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Choose the best plan</span> — pick
              the coverage that fits your needs and budget.
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-center text-gray-400">
        You'll be redirected to Chaiz to complete your extended warranty quote.
      </p>
    </div>
  );
};

export default ExtendedWarrantyHandoff;
